// src/pages/POS.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { authorizedUrl } from '../utils/authLink';
import BarcodeScannerModal from '../components/BarcodeScannerModal';

/* ----------------------------- Small utilities ----------------------------- */
const clampNum = (v, lo, hi) => Math.max(lo, Math.min(hi, Number(v || 0)));
const fmt = (n) => Number(n || 0).toFixed(2);

/* ----------------------------- Theme helpers ------------------------------- */
// Use LIGHT fallbacks; your dark theme CSS vars will override these.
const CSS = {
  border:  '1px solid var(--border, #e5e7eb)',
  cardBg:  'var(--card-bg, #ffffff)',
  cardFg:  'var(--card-fg, #0f172a)',
  surface: 'var(--surface, #ffffff)',
  subtle:  'var(--subtle-bg, #f8fafc)',
  chipBg:  'var(--code-bg, #eef2ff)',
  chipAlt: 'var(--code-bg, #f1f5f9)',
  text:    'var(--text, #0f172a)',
  muted:   'var(--muted, #64748b)',
  hover:   'var(--hover, rgba(99,102,241,0.08))',
  accent:  'var(--accent, #6366f1)',
  accentFg:'var(--accent-contrast, #ffffff)',
};

// Phone helpers (mirrors Customers page rules)
const digitsOnly = (p) => String(p || '').replace(/\D/g, '');
function stripCountryOrTrunk(raw) {
  let s = String(raw || '').replace(/\D/g, '');
  if (s.startsWith('0091')) s = s.slice(4);
  else if (s.length > 10 && s.startsWith('91')) s = s.slice(2);
  while (s.startsWith('0')) s = s.slice(1);
  return s;
}
const phoneKey = (raw) => stripCountryOrTrunk(raw);
function classifyINPhone(raw) {
  const only = digitsOnly(raw);
  if (!only) return { ok: false, type: null, nsn: '', e164: '' };
  const nsn = stripCountryOrTrunk(only);
  if (/^[6-9]\d{9}$/.test(nsn)) return { ok: true, type: 'mobile', nsn, e164: `+91${nsn}` };
  if (/^\d{8,10}$/.test(nsn)) return { ok: true, type: 'landline', nsn, e164: `+91${nsn}` };
  return { ok: false, type: null, nsn, e164: '' };
}

/* ----------------------------- Customer Search ----------------------------- */
function CustomerSearch({ onPick, onWalkIn, focusRef, setInfoOpen, onCreateCustomer }) {
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');
  const [createForm, setCreateForm] = useState({ name: '', phone: '', state_code: '', email: '' });
  const [dupPhone, setDupPhone] = useState(false);
  const [checkingDup, setCheckingDup] = useState(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/customers', { params: { search: q } });
        setSuggestions(data || []);
        setOpen(true);
        setHi(data && data.length ? 0 : -1);
      } catch {}
      finally { setLoading(false); }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const choose = (c) => { setQ(''); setSuggestions([]); setOpen(false); onPick?.(c); };
  useEffect(() => { if (!q.trim()) { setShowCreate(false); setCreateErr(''); } }, [q]);

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(i => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(i => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hi >= 0) choose(suggestions[hi]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const openCreate = () => {
    const trimmed = q.trim();
    const digits = trimmed.replace(/\D/g, '');
    setCreateForm({
      name: digits === trimmed ? '' : trimmed,
      phone: digits,
      state_code: '',
      email: '',
    });
    setCreateErr('');
    setShowCreate(true);
  };

  const onCreateField = (e) => {
    const { name, value } = e.target;
    if (name === 'state_code') return setCreateForm((f) => ({ ...f, state_code: String(value || '').toUpperCase() }));
    setCreateForm((f) => ({ ...f, [name]: value }));
  };

  const phoneDigits = createForm.phone.replace(/\D/g, '');
  const phoneInfo = classifyINPhone(createForm.phone);
  const phoneOk = phoneInfo.ok;
  const stateOk = !createForm.state_code || /^[A-Z]{2}$/.test(createForm.state_code);
  const emailOk = !createForm.email || /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(createForm.email.trim());
  const canCreate = createForm.name.trim().length >= 2 && phoneOk && stateOk && emailOk && !dupPhone;

  // Duplicate phone check (same normalization as Customers page)
  useEffect(() => {
    if (!showCreate) return;
    const key = phoneKey(createForm.phone);
    if (!key) { setDupPhone(false); return; }
    let cancel = false;
    setCheckingDup(true);
    (async () => {
      try {
        const { data } = await api.get('/customers', { params: { search: key } });
        const list = Array.isArray(data) ? data : [];
        const has = list.some((c) => phoneKey(c.phone || '') === key);
        if (!cancel) setDupPhone(has);
      } catch {
        if (!cancel) setDupPhone(false);
      } finally {
        if (!cancel) setCheckingDup(false);
      }
    })();
    return () => { cancel = true; };
  }, [createForm.phone, showCreate]);

  const submitCreate = async () => {
    if (!onCreateCustomer || !canCreate) return;
    setCreating(true); setCreateErr('');
    try {
      const payload = {
        name: createForm.name.trim(),
        phone: phoneInfo.e164 || phoneDigits || '',
        email: createForm.email.trim() || '',
        state_code: createForm.state_code.trim() || '',
      };
      const res = await onCreateCustomer(payload);
      if (res && res.customer) {
        setShowCreate(false);
        setQ('');
        setSuggestions([]);
        setOpen(false);
        onPick?.(res.customer);
      } else if (res && res.error) {
        setCreateErr(res.error);
      }
    } catch (e) {
      setCreateErr(e?.message || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={boxRef}>
      <input
        ref={focusRef}
        type="text"
        placeholder="Search customer by name or phone"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        onKeyDown={onKeyDown}
        className="input"
        style={{ width: '100%', boxSizing: 'border-box' }}
      />

      {open && suggestions.length > 0 && (
        <div
          className="card"
          style={{
            marginTop: 6, maxHeight: 240, overflowY: 'auto',
            border: CSS.border, background: CSS.cardBg, color: CSS.cardFg,
            borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
        >
          {suggestions.map((c, idx) => {
            const active = idx === hi;
            return (
              <div
                key={c.id}
                onMouseDown={() => choose(c)}
                onMouseEnter={() => setHi(idx)}
                style={{
                  padding: '8px 10px', cursor: 'pointer',
                  background: active ? CSS.accent : 'transparent',
                  color: active ? CSS.accentFg : CSS.cardFg,
                  display: 'flex', justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{c.name || '(no name)'}</div>
                  <div className="mono" style={{ fontSize: 12, opacity: 0.8 }}>{c.phone || '-'}</div>
                </div>
                <div className="mono" style={{ fontSize: 12, opacity: 0.8 }}>{c.email || ''}</div>
              </div>
            );
          })}
        </div>
      )}

      {onCreateCustomer && q.trim() && !loading && suggestions.length === 0 && (
        <div className="card" style={{ marginTop: 8, padding: 10, border: CSS.border }}>
          {!showCreate ? (
            <div className="flex" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div className="mono" style={{ fontSize: 13 }}>No customer found. Add "{q.trim()}"?</div>
              <button className="btn secondary" onClick={openCreate}>Add new</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input
                  className="input"
                  name="name"
                  placeholder="Customer name"
                  value={createForm.name}
                  onChange={onCreateField}
                  style={{ flex: '1 1 140px', minWidth: 140 }}
                />
                <input
                  className="input"
                  name="phone"
                  placeholder="Phone"
                  value={createForm.phone}
                  onChange={onCreateField}
                  style={{ flex: '1 1 140px', minWidth: 140 }}
                />
              </div>
              <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input
                  className="input"
                  name="email"
                  placeholder="Email (optional)"
                  value={createForm.email}
                  onChange={onCreateField}
                  style={{ flex: '1 1 200px', minWidth: 160 }}
                />
                <input
                  className="input mono"
                  name="state_code"
                  placeholder="State code (e.g., OD, KA)"
                  value={createForm.state_code}
                  onChange={onCreateField}
                  style={{ flex: '1 1 200px', minWidth: 160 }}
                />
              </div>
              {!phoneOk && <div className="mono" style={{ color: 'var(--danger,#b91c1c)', fontSize: 12 }}>Enter a valid 8-10 digit phone (mobile or landline).</div>}
              {dupPhone && <div className="mono" style={{ color: 'var(--danger,#b91c1c)', fontSize: 12 }}>Phone already exists.</div>}
              {checkingDup && <div className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>Checking duplicates…</div>}
              {!stateOk && <div className="mono" style={{ color: 'var(--danger,#b91c1c)', fontSize: 12 }}>State code should be 2 letters (e.g., KA).</div>}
              {!emailOk && <div className="mono" style={{ color: 'var(--danger,#b91c1c)', fontSize: 12 }}>Email looks invalid.</div>}
              {createErr && <div className="mono" style={{ color: 'var(--danger,#b91c1c)', fontSize: 12 }}>{createErr}</div>}
              <div className="flex" style={{ gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button className="btn secondary" type="button" onClick={() => { setShowCreate(false); setCreateErr(''); }}>Cancel</button>
                <button className="btn" type="button" onClick={submitCreate} disabled={!canCreate || creating}>
                  {creating ? 'Saving...' : 'Save & select'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button className="btn" onClick={() => onWalkIn?.()}>Walk-in</button>
          <button
            type="button"
            className="btn btn-sm secondary"
            onClick={() => setInfoOpen('walk_in')}
            style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
            title="Learn more about Walk-in"
          >
            ℹ️
          </button>
        </div>
        <button className="btn danger" onClick={() => setQ('')}>Clear</button>
      </div>
    </div>
  );
}

/* -------------------- One-bar Product Search + Scan button ----------------- */
function ProductSearch({ onPick, onRawEnter, onOpenScan, focusRef, setInfoOpen, categories, categoryFilter, setCategoryFilter }) {
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const matchesCategory = (p) => !categoryFilter || String(p.category_id || '') === String(categoryFilter);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { q, limit: 20000 };
        if (categoryFilter) params.category_id = categoryFilter;
        const { data } = await api.get('/products/search', { params });
        const list = Array.isArray(data) ? data : [];
        const filtered = categoryFilter ? list.filter(matchesCategory) : list;
        setSuggestions(filtered); setOpen(true); setHi(filtered.length ? 0 : -1);
      } catch {}
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, categoryFilter]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const choose = (p) => { onPick?.(p); setQ(''); setSuggestions([]); setOpen(false); setHi(-1); focusRef?.current?.focus(); };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && suggestions.length && hi >= 0) return choose(suggestions[hi]);
      const raw = String(q || '').trim();
      if (raw) onRawEnter?.(raw);
      return;
    }
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(i => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(i => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef}>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
        <select
          className="input"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ minWidth: 220 }}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {(categories || []).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="pos-search-grid" style={{ gap:8, alignItems:'center' }}>
        <input
          ref={focusRef}
          type="text"
          placeholder="Search by name / SKU / barcode (Ctrl+K), or click Scan"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onKeyDown={onKeyDown}
          className="input"
          style={{ width: '100%', boxSizing: 'border-box' }}
        />
        <button className="btn secondary" onClick={onOpenScan}>Scan</button>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('pos_scan')}
          style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Scan"
        >
          ℹ️
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="card"
          style={{
            marginTop: 6, maxHeight: 280, overflowY: 'auto',
            border: CSS.border, background: CSS.cardBg, color: CSS.cardFg,
            borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
        >
          {suggestions.map((p, idx) => {
            const active = idx === hi;
            return (
              <div
                key={p.id}
                onMouseDown={() => choose(p)}
                onMouseEnter={() => setHi(idx)}
                style={{
                  padding: '8px 10px', cursor: 'pointer',
                  background: active ? CSS.accent : 'transparent',
                  color: active ? CSS.accentFg : CSS.cardFg,
                  display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div className="mono" style={{ fontSize: 12, opacity: 0.85 }}>
                    SKU: {p.sku || '-'} · Barcode: {p.barcode || '-'}
                  </div>
                </div>
                <div className="mono" style={{ fontWeight: 600 }}>₹ {fmt(p.price)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- POS ----------------------------------- */
export default function POS() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cart, setCart] = useState([]);
  const [cartSort, setCartSort] = useState('desc'); // 'asc' | 'desc'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discountType, setDiscountType] = useState('flat'); // 'flat' | 'percent'
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('CASH'); // base method: CASH | UPI | CARD
  const [useCredit, setUseCredit] = useState(false);          // toggle to use store credit
  const [invoice, setInvoice] = useState(null);
  const [originState, setOriginState] = useState('');

  // SCAN modal (exactly like Products page: conditional render + onClose/onDetected)
  const [scanOpen, setScanOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        let data = null;
        try { data = (await api.get('/api/settings')).data; } catch {}
        if (!data) {
          try { data = (await api.get('/settings')).data; } catch {}
        }
        const os = data && typeof data === 'object' ? (data.origin_state || data.shop_state_code || '') : '';
        setOriginState(String(os || '').trim());
      } catch {}
    })();
  }, []);

  // Copy-to-clipboard for Last Invoice
  const [copied, setCopied] = useState(false);
  const copyInvoiceNumber = async () => {
    const text = String(invoice?.invoice_no || '').trim();
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),1500); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position='fixed'; ta.style.left='-9999px';
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(()=>setCopied(false),1500); }
      finally { document.body.removeChild(ta); }
    }
  };
  const tinyBtn = {
    marginLeft: 8, fontSize: 12, padding: '2px 8px',
    borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer',
  };

  // refs & shortcuts
  const findRef = useRef(null);
  const customerRef = useRef(null);
  const discountRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const p = await api.get('/products'); setProducts(p.data || []); } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try { const res = await api.get('/product-categories'); setCategories(res.data || []); } catch {}
    })();
  }, []);

  const matchesCategory = (p) => !categoryFilter || String(p.category_id || '') === String(categoryFilter);

  const stampCart = (list = []) => {
    const now = Date.now();
    return list.map((item, idx) => ({
      ...item,
      addedAt: item?.addedAt ?? item?.created_at ?? item?.createdAt ?? (now - (list.length - idx))
    }));
  };

  // Restore/persist POS state
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('pos_state') || '{}');
      if (saved.cart) setCart(stampCart(saved.cart));
      if (saved.customer) setSelectedCustomer(saved.customer);
      if (saved.discount != null) setDiscount(saved.discount);
      if (saved.discountType) setDiscountType(saved.discountType);
      if (saved.useCredit != null) setUseCredit(!!saved.useCredit);
      if (saved.paymentMethod) setPaymentMethod(saved.paymentMethod);
      if (saved.cartSort) setCartSort(saved.cartSort === 'asc' ? 'asc' : 'desc');
    } catch {}
  }, []);
  useEffect(() => {
    const state = { cart, customer: selectedCustomer, discount, discountType, useCredit, paymentMethod, cartSort };
    localStorage.setItem('pos_state', JSON.stringify(state));
  }, [cart, selectedCustomer, discount, discountType, useCredit, paymentMethod, cartSort]);

  const getAddedAt = (item, idx) => {
    const ts = Number(item?.addedAt ?? item?.created_at ?? item?.createdAt ?? NaN);
    return Number.isFinite(ts) ? ts : idx;
  };
  const sortCartByAdded = (list, direction = 'desc') => {
    const dir = direction === 'asc' ? 1 : -1;
    return list
      .map((item, idx) => ({ item, idx, ts: getAddedAt(item, idx) }))
      .sort((a, b) => {
        const delta = (a.ts - b.ts) * dir;
        if (delta !== 0) return delta;
        return (a.idx - b.idx) * dir;
      })
      .map(({ item }) => item);
  };
  const orderedCart = useMemo(() => sortCartByAdded(cart, cartSort), [cart, cartSort]);

  const createCustomerFromPos = async (payload) => {
    try {
      const { data } = await api.post('/customers', payload);
      const created = data && typeof data === 'object' ? data : { ...payload, id: data?.id || Date.now() };
      setSelectedCustomer(created);
      setUseCredit(false);
      return { ok: true, customer: created };
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to create customer';
      alert(msg);
      return { ok: false, error: msg };
    }
  };

  /* ----------------------------- Cart helpers ------------------------------ */
  function addProductToCart(p) {
    if (!p) return;
    setCart(prev => {
      const idx = prev.findIndex(i => i.product_id === p.id);
      if (idx >= 0) {
        const copy = [...prev];
        const updated = { ...copy[idx], qty: copy[idx].qty + 1, addedAt: copy[idx].addedAt ?? Date.now() };
        copy.splice(idx, 1);
        return [updated, ...copy];
      }
      return [{
        product_id: p.id, name: p.name, sku: p.sku,
        price: Number(p.price || 0), gst_rate: Number(p.gst_rate || 0), qty: 1,
        discount_pct: Number(p.discount_pct || 0), note: '', addedAt: Date.now()
      }, ...prev];
    });
  }

  // Resolve text/barcode exactly like a cashier scan would
  const resolveAndAdd = async (raw) => {
    const q = String(raw || '').trim();
    if (!q) return;

    // 0) local exact match (fast path)
    const local = products.find(p => matchesCategory(p) && (String(p.barcode || '') === q || String(p.sku || '') === q));
    if (local) { addProductToCart(local); findRef.current?.focus(); return; }

    // 1) backend lookup (if you have it)
    try {
      const { data } = await api.get('/products/lookup', { params: { code: q } });
      const found = Array.isArray(data) ? data[0] : data;
      if (found && matchesCategory(found)) { addProductToCart(found); findRef.current?.focus(); return; }
    } catch {}

    // 2) explicit probes
    try {
      const r = await api.get('/products', { params: { barcode: q } });
      const found = Array.isArray(r.data) ? r.data[0] : r.data;
      if (found && matchesCategory(found)) { addProductToCart(found); findRef.current?.focus(); return; }
    } catch {}
    try {
      const r = await api.get('/products', { params: { sku: q } });
      const found = Array.isArray(r.data) ? r.data[0] : r.data;
      if (found && matchesCategory(found)) { addProductToCart(found); findRef.current?.focus(); return; }
    } catch {}

    // 3) fallback to search list (pick exact first)
    try {
      const params = { q, limit: 20000 };
      if (categoryFilter) params.category_id = categoryFilter;
      const { data } = await api.get('/products/search', { params });
      const list = Array.isArray(data) ? data : [];
      const filtered = categoryFilter ? list.filter(matchesCategory) : list;
      const exact = filtered.find(p => String(p.barcode || '') === q || String(p.sku || '') === q);
      const pick = exact || filtered[0];
      if (pick) { addProductToCart(pick); findRef.current?.focus(); return; }
    } catch {}

    alert('Product not found');
  };

  function incQty(id, step = 1) {
    setCart(prev => prev.map(i => i.product_id === id ? { ...i, qty: clampNum(i.qty + step, 1, 999999) } : i));
  }
  function setQty(id, qty) {
    setCart(prev => prev.map(i => i.product_id === id ? { ...i, qty: clampNum(qty, 1, 999999) } : i));
  }

  // --- Minimal UX fixes: allow temporarily empty values ("" or ".") while typing ---
  const isBlankEdit = (v) => v === '' || v === '.';

  function setPrice(id, price) {
    setCart(prev => prev.map(i => {
      if (i.product_id !== id) return i;
      const v = price;
      return {
        ...i,
        price: isBlankEdit(v) ? '' : clampNum(v, 0, 99999999)
      };
    }));
  }
  function setGst(id, rate) {
    setCart(prev => prev.map(i => {
      if (i.product_id !== id) return i;
      const v = rate;
      return {
        ...i,
        gst_rate: isBlankEdit(v) ? '' : clampNum(v, 0, 100)
      };
    }));
  }
  function setDisc(id, pct) {
    setCart(prev => prev.map(i => {
      if (i.product_id !== id) return i;
      const v = pct;
      return {
        ...i,
        discount_pct: isBlankEdit(v) ? '' : clampNum(v, 0, 100)
      };
    }));
  }
  // ---------------------------------------------------------------------------

  function setNote(id, note) {
    setCart(prev => prev.map(i => i.product_id === id ? { ...i, note } : i));
  }
  function removeLine(id) { setCart(prev => prev.filter(i => i.product_id !== id)); }
  function clearCart() { if (cart.length && window.confirm('Clear cart?')) setCart([]); }

  /* ----------------------------- Totals math ------------------------------- */
  const netUnit = (i) => Number(i.price || 0) * (1 - Math.max(0, Math.min(100, Number(i.discount_pct || 0))) / 100);
  const lineGross = (i) => i.qty * netUnit(i) * (1 + Number(i.gst_rate || 0) / 100);
  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.qty * i.price, 0), [cart]);
  const gst = useMemo(() => cart.reduce((s, i) => s + i.qty * netUnit(i) * (Number(i.gst_rate || 0) / 100), 0), [cart]);
  const lineDiscountTotal = useMemo(() => cart.reduce((s, i) => {
    const full = i.qty * Number(i.price || 0);
    const net  = i.qty * netUnit(i);
    return s + Math.max(0, full - net);
  }, 0), [cart]);
  const netSubtotal = useMemo(() => Math.max(0, subtotal - lineDiscountTotal), [subtotal, lineDiscountTotal]);
  const total = useMemo(() => Math.max(0, netSubtotal + gst), [netSubtotal, gst]);

  // tax type
  const shopCode = String(originState || '').trim();
  const custCode = String(selectedCustomer?.state_code || '').trim();
  // Fallback rule: if either code is missing, assume customer is same as shop (treat as Intra-state)
  const effShop = shopCode || custCode;  // if shop missing but customer present, assume shop = customer
  const effCust = custCode || shopCode;  // if customer missing, assume customer = shop
  const haveAny = !!(effShop && effCust);
  const intra = haveAny ? (effShop === effCust) : true; // if both missing, default to intra
  const taxType = haveAny ? (intra ? 'Intra-state' : 'Inter-state') : 'Intra-state';

  // Robust split with rounding so parts always sum to total GST
  const gstRounded = Number(Number(gst).toFixed(2));
  const half = Number((gstRounded / 2).toFixed(2));
  const diff = Number((gstRounded - (half * 2)).toFixed(2)); // may be 0.01 or -0.01 depending on rounding
  const cgstAmt = intra ? Number((half + diff).toFixed(2)) : 0;
  const sgstAmt = intra ? half : 0;
  const igstAmt = intra ? 0 : gstRounded;

  const creditAvailable = Number(selectedCustomer?.store_credit || 0);

  // ---- Payment summary (drives UI and checkout payload) ----
  const { isFullCredit, creditUsed, collectNow, mode } = useMemo(() => {
    if (!useCredit || !selectedCustomer) {
      return {
        isFullCredit: false,
        creditUsed: 0,
        collectNow: total,
        mode: paymentMethod, // CASH | UPI | CARD
      };
    }
    const use = Math.min(creditAvailable, total);
    const rem = Math.max(0, total - use);
    const full = rem === 0;
    return {
      isFullCredit: full,
      creditUsed: use,
      collectNow: rem,
      mode: full ? 'CREDIT' : `CREDIT+${paymentMethod}`,
    };
  }, [useCredit, selectedCustomer, creditAvailable, total, paymentMethod]);

  // Auto-highlight CREDIT pill when full credit covers the bill
  const payable = collectNow;

  // UI: collapsible GST breakdown
  const [showGstDetails, setShowGstDetails] = useState(false);

  /* -------------------------------- Checkout ------------------------------- */
  async function checkout() {
    if (!Array.isArray(cart) || cart.reduce((s, i) => s + Number(i?.qty || 0), 0) <= 0) {
      return alert('Cart empty');
    }

    const payload = {
      source: 'POS',
      customer_id: selectedCustomer?.id || null,
      // Each item carries clear discount fields:
      //  - discount_pct (percent)
      //  - discount_per_item (currency per unit)
      //  - discount (line total = qty * discount_per_item)
      //  - net_price (unit AFTER discount)
      items: sortCartByAdded(cart, 'asc').map(i => {
        const unitPrice = Number(i.price || 0);
        const unitNet   = Number(netUnit(i));
        const perItem   = Number((unitPrice - unitNet).toFixed(2));
        const lineDisc  = Number((i.qty * perItem).toFixed(2));
        return {
          product_id: i.product_id,
          qty: i.qty,
          price: unitPrice,                    // BEFORE discount (per unit)
          discount_pct: Number(i.discount_pct || 0),
          discount_per_item: perItem,          // currency off per unit
          discount: lineDisc,                  // line discount amount
          net_price: unitNet,                  // AFTER discount (per unit)
          gst_rate: Number(i.gst_rate || 0),
          note: (i.note || '').trim()
        };
      }),
      discount: Number(lineDiscountTotal.toFixed(2)),
      // Back-compat fields (keep your existing API happy)
      payment_method: mode, // "CREDIT", "CREDIT+UPI", "CREDIT+CARD", etc.
      use_credit: !!useCredit,
      applied_credit: Number(creditUsed || 0),
      // New richer structure (if your backend supports it)
      payments: (
        isFullCredit
          ? [{ method: 'CREDIT', amount: Number(creditUsed.toFixed(2)) }]
          : useCredit
              ? [
                  { method: 'CREDIT', amount: Number(creditUsed.toFixed(2)) },
                  { method: paymentMethod, amount: Number(collectNow.toFixed(2)) },
                ]
              : [{ method: paymentMethod, amount: Number(collectNow.toFixed(2)) }]
      ),
      payment_mode_label: mode, // handy for printing/records (e.g., "CREDIT+CASH")
    };

    try {
      const { data } = await api.post('/sales', payload);
      setInvoice(data);
      setCart([]); setDiscount(0); setDiscountType('flat'); setUseCredit(false);
      window.scrollTo(0, 0);
    } catch (e) {
      alert(e?.response?.data?.error || 'Checkout failed');
    }
  }

  /* -------- FIX: stale-closure on F2 (always call latest checkout) --------- */
  const checkoutRef = useRef(() => {});
  useEffect(() => { checkoutRef.current = checkout; }, [checkout]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === 'k') { e.preventDefault(); findRef.current?.focus(); }
      if (e.key === 'F2') { e.preventDefault(); checkoutRef.current(); }
      if (e.key === 'F7') { e.preventDefault(); discountRef.current?.focus(); }
      if (e.key === 'F8') { e.preventDefault(); customerRef.current?.focus(); }
      if (e.key === 'F9') {
        e.preventDefault();
        setPaymentMethod(pm => pm === 'CASH' ? 'UPI' : pm === 'UPI' ? 'CARD' : 'CASH');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // one subscription; handler uses ref

  const pdfHref = invoice ? authorizedUrl(`/api/sales/${invoice.id}/pdf`) : '#';

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="container" style={{ marginTop: 12 }}>
      <div className="pos-shell">
        {/* ----------------------------- LEFT: CART ---------------------------- */}
        <div>
          {/* Fast add row: ONE BAR + SCAN (exactly like Products) */}
          <div className="card" style={{ marginBottom: 12 }}>
            <div
              style={{
                border: CSS.border, borderRadius: 10, background: CSS.subtle,
                padding: 10, overflow: 'visible',
              }}
            >
              <div style={{ display: 'grid', gap: 8 }}>
                <ProductSearch
                  onPick={addProductToCart}
                  onRawEnter={resolveAndAdd}
                  onOpenScan={() => setScanOpen(true)}
                  focusRef={findRef}
                  setInfoOpen={setInfoOpen}
                  categories={categories}
                  categoryFilter={categoryFilter}
                  setCategoryFilter={setCategoryFilter}
                />
              </div>
            </div>

            <div className="flex pos-actions" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn secondary" onClick={() => findRef.current?.focus()}>Focus Search (Ctrl+K)</button>
              <button className="btn danger" onClick={clearCart} disabled={!cart.length}>Clear Cart</button>
            </div>
          </div>

          {/* Cart table */}
          <div className="card pos-cart">
            <div className="flex" style={{ alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>Cart</h3>
              <div className="muted" style={{ marginLeft: 8 }}>Items: <b>{cart.reduce((s,i)=>s+i.qty,0)}</b></div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label className="mono" style={{ fontSize: 12 }}>Sort</label>
                <select
                  className="input mono"
                  value={cartSort}
                  onChange={(e) => setCartSort(e.target.value === 'asc' ? 'asc' : 'desc')}
                  style={{ width: 160, padding: '4px 8px' }}
                >
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </select>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>SKU</th>
                    <th style={{ width: 140 }}>Qty</th>
                    <th style={{ width: 120 }}>Price</th>
                    <th style={{ width: 110 }}>Disc %</th>
                    <th style={{ width: 110 }}>GST %</th>
                    <th style={{ width: 140, textAlign:'right' }}>Amount</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {orderedCart.map(i => (
                    <React.Fragment key={i.product_id}>
                      <tr>
                        <td data-label="Item"><div>{i.name}</div></td>
                        <td data-label="SKU" className="mono">{i.sku}</td>
                        <td data-label="Qty">
                          <div className="flex" style={{ gap: 6 }}>
                            <button className="btn btn-sm secondary" onClick={() => incQty(i.product_id, -1)}>-</button>
                            <input
                              className="input mono"
                              type="number" min="1"
                              value={i.qty}
                              onChange={e => setQty(i.product_id, e.target.value)}
                              style={{ width: 70 }}
                            />
                            <button className="btn btn-sm secondary" onClick={() => incQty(i.product_id, +1)}>+</button>
                          </div>
                        </td>
                        <td data-label="Price">
                          <input
                            className="input mono"
                            type="number" min="0" step="1"
                            value={i.price}
                            onChange={e => setPrice(i.product_id, e.target.value)}
                            style={{ width: 110 }}
                          />
                        </td>
                        <td data-label="Disc %">
                          <input
                            className="input mono"
                            type="number" min="0" max="100" step="1"
                            value={i.discount_pct === '' ? '' : Number(i.discount_pct || 0)}
                            onChange={e => setDisc(i.product_id, e.target.value)}
                            style={{ width: 90 }}
                          />
                        </td>
                        <td data-label="GST %">
                          <input
                            className="input mono"
                            type="number" min="0" max="100" step="1"
                            value={i.gst_rate === '' ? '' : Number(i.gst_rate || 0)}
                            onChange={e => setGst(i.product_id, e.target.value)}
                            style={{ width: 90 }}
                          />
                        </td>
                        <td data-label="Amount" className="mono"><span className="amount-value">₹ {fmt(lineGross(i))}</span></td>
                        <td data-label="Actions">
                          <button className="btn btn-sm danger" onClick={() => removeLine(i.product_id)}>×</button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={7}>
                          <textarea
                            className="input pos-note"
                            value={i.note || ''}
                            onChange={e => setNote(i.product_id, e.target.value)}
                            placeholder="Add note…"
                            style={{
                              marginTop: 6,
                              width: '100%',
                              fontSize: 16,
                              padding: '8px 10px',
                              minHeight: 40,
                              resize: 'vertical'
                            }}
                            aria-label={`Note for ${i.name}`}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  {!cart.length && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', opacity: 0.7 }}>No items</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ---------------------------- RIGHT: SIDEBAR ------------------------- */}
        <div className="pos-sidebar">
          {/* Customer + Payment */}
          <div className="card">
            <div className="mono" style={{ marginBottom: 8 }}>Customer</div>
            <CustomerSearch
              onPick={(c) => setSelectedCustomer(c)}
              onWalkIn={() => setSelectedCustomer(null)}
              focusRef={customerRef}
              setInfoOpen={setInfoOpen}
              onCreateCustomer={createCustomerFromPos}
            />
            {selectedCustomer ? (
              <div className="mono" style={{ marginTop: 8, padding: '6px 8px', background: CSS.chipBg, borderRadius: 6 }}>
                {selectedCustomer.name}{selectedCustomer.phone ? ' — ' + selectedCustomer.phone : ''}
              </div>
            ) : (
              <div className="mono" style={{ marginTop: 8, padding: '6px 8px', background: CSS.chipAlt, borderRadius: 6 }}>
                Walk-in
              </div>
            )}

            {/* Payment selection */}
            <div className="mono" style={{ marginTop: 12 }}>Payment</div>
            <div className="flex" style={{ gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {['CASH','UPI','CARD'].map(pm => (
                <button
                  key={pm}
                  className={`btn ${(!isFullCredit && paymentMethod === pm) ? '' : 'secondary'}`}
                  onClick={() => setPaymentMethod(pm)}
                  title={useCredit ? `Split: CREDIT + ${pm}` : pm}
                >
                  {pm}
                </button>
              ))}
              {/* CREDIT pill (becomes active automatically if full credit) */}
              <button
                className={`btn ${isFullCredit ? '' : 'secondary'}`}
                onClick={() => setUseCredit(true)}
                disabled={!selectedCustomer || creditAvailable <= 0}
                title="Pay fully by credit (auto when credit covers total)"
              >
                CREDIT
              </button>
            </div>
          </div>

          {/* Totals */}
          <div className="card pos-totals" style={{ marginTop: 12 }}>
            <div className="grid auto-grid-2" style={{ gap: 10 }}>
              <div className="mono">Subtotal</div>
              <div className="mono" style={{ textAlign: 'right' }}>₹ {fmt(subtotal)}</div>

              <div className="mono">Total Discount</div>
              <div className="mono" style={{ textAlign: 'right', color: '#065f46' }}>- ₹ {fmt(lineDiscountTotal)}</div>

              <div className="mono">Tax Type</div>
              <div className="mono" style={{ textAlign: 'right' }}>{taxType || '—'}</div>

              <div className="mono">GST</div>
              <div className="mono" style={{ textAlign: 'right' }}>₹ {fmt(gstRounded)}</div>
            </div>

            {/* Dotted divider + collapsible breakdown (makes it clear these are components of GST) */}
            <div className="border-t" style={{ borderTop: '1px dotted var(--border, #94a3b8)', margin: '8px 0' }} />

            <button
              type="button"
              onClick={() => setShowGstDetails(v => !v)}
              className="btn secondary"
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              aria-expanded={showGstDetails}
              aria-controls="gst-breakdown"
            >
              <span className="mono">GST breakdown (components)</span>
              <span className="mono" style={{ fontSize: 12 }}>{showGstDetails ? '▲' : '▼'}</span>
            </button>

            {showGstDetails && (
              <div id="gst-breakdown" className="grid auto-grid-3" style={{ gap: 8, marginTop: 8 }}>
                <div className="card" style={{ padding: 8 }}>
                  <div className="mono" style={{ opacity: 0.85 }}>IGST</div>
                  <div className="mono" style={{ fontWeight: 600 }}>₹ {fmt(igstAmt)}</div>
                </div>
                <div className="card" style={{ padding: 8 }}>
                  <div className="mono" style={{ opacity: 0.85 }}>CGST</div>
                  <div className="mono" style={{ fontWeight: 600 }}>₹ {fmt(cgstAmt)}</div>
                </div>
                <div className="card" style={{ padding: 8 }}>
                  <div className="mono" style={{ opacity: 0.85 }}>SGST</div>
                  <div className="mono" style={{ fontWeight: 600 }}>₹ {fmt(sgstAmt)}</div>
                </div>
              </div>
            )}

            <div className="grid auto-grid-2" style={{ gap: 10, marginTop: 10 }}>
              <div className="mono">Credit Available</div>
              <div className="mono" style={{ textAlign: 'right' }}>₹ {fmt(creditAvailable)}</div>

              <div className="mono">
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!useCredit}
                    onChange={e => setUseCredit(e.target.checked)}
                    disabled={!selectedCustomer || creditAvailable <= 0}
                  />
                  Pay from credit
                </label>
              </div>
              <div className="mono" style={{ textAlign: 'right' }}>₹ {fmt(creditUsed)}</div>

              <div className="mono" style={{ borderTop: '1px dashed var(--border, #475569)', paddingTop: 8, fontWeight: 700 }}>Total</div>
              <div className="mono" style={{ textAlign: 'right', borderTop: '1px dashed var(--border, #475569)', paddingTop: 8, fontWeight: 700 }}>₹ {fmt(total)}</div>

              <div className="mono" style={{ fontWeight: 700 }}>Payable</div>
              <div className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>₹ {fmt(payable)}</div>
            </div>

            {/* Mode hint */}
            <div className="mono" style={{ marginTop: 6, fontSize: 12, color: '#475569' }}>
              Mode: <b>{mode}</b>
              {isFullCredit && <span style={{ color:'#065f46' }}> · covered fully by credit</span>}
              {useCredit && !isFullCredit && <span> · remainder via <b>{paymentMethod}</b></span>}
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn primary" onClick={checkout} disabled={!cart.length}>Checkout (F2)</button>
              <button className="btn secondary" onClick={() => window.print()} disabled={!cart.length}>Print (browser)</button>
            </div>
          </div>

          {/* Last invoice */}
          <div className="card" style={{ marginTop: 12 }}>
            <div className="mono" style={{ marginBottom: 6 }}>Last Invoice</div>
            {invoice ? (
              <div>
                <div className="mono" style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                  <span title="Click to copy invoice number" onClick={copyInvoiceNumber} style={{ cursor:'pointer', userSelect:'none' }}>
                    {invoice.invoice_no}
                  </span>
                  <span>· ₹ {fmt(invoice.total)}</span>
                  <button type="button" aria-label="Copy invoice number" title="Copy invoice number" onClick={copyInvoiceNumber} style={tinyBtn}>
                    📋 Copy
                  </button>
                  {copied && <span className="mono" style={{ fontSize: 12, color: '#065f46' }}>✓ Copied!</span>}
                </div>
                <div style={{ marginTop: 8 }}>
                  <a className="btn" href={pdfHref} target="_blank" rel="noreferrer">Open PDF</a>
                </div>
              </div>
            ) : (
              <div className="mono" style={{ opacity: 0.8 }}>No invoice yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Info Modals */}
      {infoOpen === 'pos_scan' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Product Scan</div>
              <button
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text, #0f172a)' }}>
              <p><strong>Scan</strong> button allows you to quickly search and add products to your POS cart using barcode scanning.</p>
              <p><strong>How it works:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Click the Scan button in the product search bar</li>
                <li>Point your camera at a product barcode</li>
                <li>The barcode will be automatically detected and scanned</li>
                <li>The matching product will be instantly added to your cart</li>
                <li>The quantity will increase if you scan the same product again</li>
              </ul>
              <p><strong>Tips:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Ensure good lighting for accurate barcode detection</li>
                <li>Hold the camera steady at a 45-degree angle</li>
                <li>You can also scan by SKU code if barcode is not available</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {infoOpen === 'walk_in' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Walk-in Customer</div>
              <button
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text, #0f172a)' }}>
              <p><strong>Walk-in</strong> mode allows you to process sales from customers who are not registered in your system.</p>
              <p><strong>When to use:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>For one-time or occasional customers</li>
                <li>When customer details are not available</li>
                <li>For quick retail transactions at your counter</li>
                <li>When you don't need to track customer purchase history</li>
              </ul>
              <p><strong>Benefits:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>No customer lookup required</li>
                <li>Faster checkout process</li>
                <li>No store credit tracking</li>
                <li>Ideal for cash transactions</li>
              </ul>
              <p><strong>Note:</strong> Walk-in customers cannot use store credit, as there's no account history to track.</p>
            </div>
          </div>
        </div>
      )}

      {/* Scanner modal — same contract as Products page */}
      {scanOpen && (
        <BarcodeScannerModal
          onClose={() => setScanOpen(false)}
          onDetected={(code) => {
            const v = (code || '').trim();
            setScanOpen(false);
            if (v) resolveAndAdd(v);
          }}
        />
      )}
    </div>
  );
}
